import type { WeatherHour, WeatherResponse } from '@omnia/shared';

export type ConditionIcon =
  | 'sun'
  | 'moon'
  | 'cloud-sun'
  | 'cloud-moon'
  | 'cloud'
  | 'fog'
  | 'drizzle'
  | 'rain'
  | 'snow'
  | 'storm'
  | 'hail';

export interface Condition {
  label: string;
  icon: ConditionIcon;
}

/** WMO weather interpretation codes as returned by Open-Meteo. */
export function describeCondition(code: number, isDay: boolean): Condition {
  switch (code) {
    case 0:
      return { label: 'Açık', icon: isDay ? 'sun' : 'moon' };
    case 1:
      return { label: 'Çoğunlukla açık', icon: isDay ? 'sun' : 'moon' };
    case 2:
      return { label: 'Parçalı bulutlu', icon: isDay ? 'cloud-sun' : 'cloud-moon' };
    case 3:
      return { label: 'Kapalı', icon: 'cloud' };
    case 45:
    case 48:
      return { label: 'Sisli', icon: 'fog' };
    case 51:
    case 53:
    case 55:
      return { label: 'Çisenti', icon: 'drizzle' };
    case 56:
    case 57:
      return { label: 'Dondurucu çisenti', icon: 'drizzle' };
    case 61:
      return { label: 'Hafif yağmur', icon: 'rain' };
    case 63:
      return { label: 'Yağmur', icon: 'rain' };
    case 65:
      return { label: 'Kuvvetli yağmur', icon: 'rain' };
    case 66:
    case 67:
      return { label: 'Dondurucu yağmur', icon: 'rain' };
    case 71:
      return { label: 'Hafif kar', icon: 'snow' };
    case 73:
      return { label: 'Kar', icon: 'snow' };
    case 75:
      return { label: 'Yoğun kar', icon: 'snow' };
    case 77:
      return { label: 'Kar taneleri', icon: 'snow' };
    case 80:
    case 81:
    case 82:
      return { label: 'Sağanak yağmur', icon: 'rain' };
    case 85:
    case 86:
      return { label: 'Kar sağanağı', icon: 'snow' };
    case 95:
      return { label: 'Gök gürültülü fırtına', icon: 'storm' };
    case 96:
    case 99:
      return { label: 'Dolu ve fırtına', icon: 'hail' };
    default:
      return { label: 'Bilinmiyor', icon: 'cloud' };
  }
}

/**
 * The hours to preview: the next round hour, then every `step` hours after it (17:00, 20:00, 23:00
 * at 16:43). Times are the location's local time, so string order is chronological order.
 */
export function pickForecastChips(hourly: WeatherHour[], nowLocal: string, count = 3, step = 3): WeatherHour[] {
  const first = hourly.findIndex((hour) => hour.time > nowLocal);
  if (first < 0) return [];
  const chips: WeatherHour[] = [];
  for (let index = first; index < hourly.length && chips.length < count; index += step) chips.push(hourly[index]!);
  return chips;
}

/** `HH:mm` of a `YYYY-MM-DDTHH:mm` local time. */
export const hourLabel = (time: string) => time.slice(11, 16);

const RAIN_CHANCE_NOTICE = 40;

export type PrecipitationSummary =
  | { kind: 'now'; mm: number }
  | { kind: 'chance'; percent: number }
  | { kind: 'none' };

/** Rain falling now wins; otherwise a likely shower in the next few hours; otherwise none. */
export function summarizePrecipitation(weather: Pick<WeatherResponse, 'current' | 'hourly'>): PrecipitationSummary {
  if (weather.current.precipitationMm > 0) return { kind: 'now', mm: weather.current.precipitationMm };
  const upcoming = weather.hourly
    .filter((hour) => hour.time > weather.current.time)
    .slice(0, 3)
    .map((hour) => hour.precipitationProbability ?? 0);
  const peak = Math.max(0, ...upcoming);
  return peak >= RAIN_CHANCE_NOTICE ? { kind: 'chance', percent: Math.round(peak) } : { kind: 'none' };
}
